import { API_BASE } from "../config/api";
import {
  OCRResponse,
  Order,
  OrderCreateRequest,
  OrderFilters,
  OrderListResponse,
  OrderUpdateRequest,
  Product,
  ProductAddon,
  ProductAddonBulkUpsertRequest,
  ProductAddonBulkUpsertResponse,
  ProductAddonCreateRequest,
  ProductAddonFilters,
  ProductAddonListResponse,
  ProductAddonUpdateRequest,
  ProductBulkUpsertRequest,
  ProductBulkUpsertResponse,
  ProductCreateRequest,
  ProductFilters,
  ProductGetListResponse,
  ProductUpdateRequest,
} from "../types/order";
import { authFetcher } from "../utils/auth-fetch";

const BASE_ADMIN = API_BASE.ORDER_ADMIN;
const BASE_CUSTOMER = API_BASE.ORDER_CUSTOMER;

/** @section Orders API — Admin */
const getAdminOrders = async (params: OrderFilters = {}) => {
  return await authFetcher<OrderListResponse>(
    "GET",
    `${BASE_ADMIN}/orders`,
    null,
    { ...params },
  );
};

const getFilenameFromContentDisposition = (
  contentDisposition: string | undefined,
  fallbackFilename: string,
) => {
  const filenameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i,
  );
  return filenameMatch?.[1]
    ? decodeURIComponent(filenameMatch[1])
    : fallbackFilename;
};

const exportAdminOrders = async (params: OrderFilters = {}) => {
  const response = await authFetcher<Blob>(
    "GET",
    `${BASE_ADMIN}/orders/export`,
    null,
    { ...params },
    null,
    false,
    undefined,
    undefined,
    "blob",
  );

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const filename = getFilenameFromContentDisposition(
    response.headers?.["content-disposition"],
    `essilor-orders_${new Date().toISOString().slice(0, 10)}.csv`,
  );
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const createAdminOrder = async (data: OrderCreateRequest) => {
  return await authFetcher<Order>("POST", `${BASE_ADMIN}/orders`, data);
};

const getAdminOrder = async (id: string) => {
  return await authFetcher<Order>("GET", `${BASE_ADMIN}/orders/${id}`);
};

const updateAdminOrder = async (id: string, data: OrderUpdateRequest) => {
  return await authFetcher<void>("PATCH", `${BASE_ADMIN}/orders/${id}`, data);
};

const deleteAdminOrder = async (id: string) => {
  return await authFetcher<void>("DELETE", `${BASE_ADMIN}/orders/${id}`);
};

/** @section Orders API — Customer */
const getCustomerOrders = async (params: OrderFilters = {}) => {
  return await authFetcher<OrderListResponse>(
    "GET",
    `${BASE_CUSTOMER}/orders`,
    null,
    { ...params },
  );
};

const createCustomerOrder = async (data: OrderCreateRequest) => {
  return await authFetcher<Order>("POST", `${BASE_CUSTOMER}/orders`, data);
};

const getCustomerOrder = async (id: string) => {
  return await authFetcher<Order>("GET", `${BASE_CUSTOMER}/orders/${id}`);
};

const updateCustomerOrder = async (id: string, data: OrderUpdateRequest) => {
  return await authFetcher<void>(
    "PATCH",
    `${BASE_CUSTOMER}/orders/${id}`,
    data,
  );
};

/** @section Products API — Admin */
const getAdminProducts = async (params: ProductFilters = {}) => {
  return await authFetcher<ProductGetListResponse>(
    "GET",
    `${BASE_ADMIN}/products`,
    null,
    { ...params },
  );
};

const createAdminProduct = async (data: ProductCreateRequest) => {
  return await authFetcher<Product>("POST", `${BASE_ADMIN}/products`, data);
};

const bulkUpsertAdminProducts = async (data: ProductBulkUpsertRequest) => {
  return await authFetcher<ProductBulkUpsertResponse>(
    "POST",
    `${BASE_ADMIN}/products/bulk-upsert`,
    data,
  );
};

const getAdminProduct = async (id: string) => {
  return await authFetcher<Product>("GET", `${BASE_ADMIN}/products/${id}`);
};

const updateAdminProduct = async (id: string, data: ProductUpdateRequest) => {
  return await authFetcher<void>("PATCH", `${BASE_ADMIN}/products/${id}`, data);
};

const deleteAdminProduct = async (id: string) => {
  return await authFetcher<void>("DELETE", `${BASE_ADMIN}/products/${id}`);
};

/** @section Products API — Customer */
const getCustomerProducts = async (params: ProductFilters = {}) => {
  return await authFetcher<ProductGetListResponse>(
    "GET",
    `${BASE_CUSTOMER}/products`,
    null,
    { ...params },
  );
};

const getCustomerProduct = async (id: string) => {
  return await authFetcher<Product>("GET", `${BASE_CUSTOMER}/products/${id}`);
};

/** @section Product Addons API — Admin */
const getAdminProductAddons = async (params: ProductAddonFilters = {}) => {
  return await authFetcher<ProductAddonListResponse>(
    "GET",
    `${BASE_ADMIN}/product-addons`,
    null,
    { ...params },
  );
};

const createAdminProductAddon = async (data: ProductAddonCreateRequest) => {
  return await authFetcher<ProductAddon>(
    "POST",
    `${BASE_ADMIN}/product-addons`,
    data,
  );
};

const bulkUpsertAdminProductAddons = async (
  data: ProductAddonBulkUpsertRequest,
) => {
  return await authFetcher<ProductAddonBulkUpsertResponse>(
    "POST",
    `${BASE_ADMIN}/product-addons/bulk-upsert`,
    data,
  );
};

const getAdminProductAddon = async (id: string) => {
  return await authFetcher<ProductAddon>(
    "GET",
    `${BASE_ADMIN}/product-addons/${id}`,
  );
};

const updateAdminProductAddon = async (
  id: string,
  data: ProductAddonUpdateRequest,
) => {
  return await authFetcher<void>(
    "PATCH",
    `${BASE_ADMIN}/product-addons/${id}`,
    data,
  );
};

const deleteAdminProductAddon = async (id: string) => {
  return await authFetcher<void>(
    "DELETE",
    `${BASE_ADMIN}/product-addons/${id}`,
  );
};

/** @section OCR API — Admin */
const processOCR = async (formData: FormData) => {
  return await authFetcher<OCRResponse>(
    "POST",
    `${BASE_ADMIN}/ocr`,
    formData,
    null,
    { "Content-Type": "multipart/form-data" },
  );
};

/** @section Product Upload API — Admin */
const uploadFinishedProducts = async (formData: FormData) => {
  return await authFetcher<ProductBulkUpsertResponse>(
    "POST",
    `${BASE_ADMIN}/products/upload/finished`,
    formData,
    null,
    { "Content-Type": "multipart/form-data" },
  );
};

const uploadRxProducts = async (formData: FormData) => {
  return await authFetcher<ProductBulkUpsertResponse>(
    "POST",
    `${BASE_ADMIN}/products/upload/rx`,
    formData,
    null,
    { "Content-Type": "multipart/form-data" },
  );
};

const uploadProductAddons = async (formData: FormData) => {
  return await authFetcher<ProductAddonBulkUpsertResponse>(
    "POST",
    `${BASE_ADMIN}/product-addons/upload`,
    formData,
    null,
    { "Content-Type": "multipart/form-data" },
  );
};

export {
  bulkUpsertAdminProductAddons,
  bulkUpsertAdminProducts,
  createAdminOrder,
  createAdminProduct,
  createAdminProductAddon,
  createCustomerOrder,
  deleteAdminOrder,
  deleteAdminProduct,
  deleteAdminProductAddon,
  exportAdminOrders,
  getAdminOrder,
  // Orders (Admin)
  getAdminOrders,
  getAdminProduct,
  getAdminProductAddon,
  // Product Addons (Admin)
  getAdminProductAddons,
  // Products (Admin)
  getAdminProducts,
  getCustomerOrder,
  // Orders (Customer)
  getCustomerOrders,
  getCustomerProduct,
  // Products (Customer)
  getCustomerProducts,
  // OCR (Admin)
  processOCR,
  updateAdminOrder,
  updateAdminProduct,
  updateAdminProductAddon,
  updateCustomerOrder,
  // Product Upload (Admin)
  uploadFinishedProducts,
  uploadProductAddons,
  uploadRxProducts,
};
